/**
 * StudentDetailCard - Individual student progress view
 *
 * Shows:
 * - Outcome breakdown with progress bars
 * - Recent assessments list
 * - Trend indicator (improving/declining/stable)
 */

import { useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  HelpCircle,
  FileText,
  Target,
  Calendar,
  User,
} from "lucide-react";
import { MasteryBadge, getMasteryColor, getMasteryTextColor } from "./MasteryBadge";
import type { StudentDetail, TrendDirection, OutcomeBreakdown, RecentAssessment } from "@/services/progress.service";
import { cn } from "@/lib/utils";

// =============================================================================
// Types
// =============================================================================

export interface StudentDetailCardProps {
  /** Student detail data */
  data: StudentDetail;
  /** Student display name (merged from local DB) */
  studentName: string;
  /** Whether this is an unknown student (no local record) */
  isUnknown?: boolean;
  /** Additional class names */
  className?: string;
  /** Called when an outcome is clicked */
  onOutcomeClick?: (outcomeId: string) => void;
  /** Called when an assessment is clicked */
  onAssessmentClick?: (scanId: string) => void;
}

// =============================================================================
// Trend Indicator Component
// =============================================================================

interface TrendIndicatorProps {
  trend: TrendDirection;
  className?: string;
}

function TrendIndicator({ trend, className }: TrendIndicatorProps) {
  const config: Record<TrendDirection, { icon: React.ElementType; label: string; className: string }> = {
    improving: {
      icon: TrendingUp,
      label: 'Improving',
      className: 'text-green-600 dark:text-green-400',
    },
    declining: {
      icon: TrendingDown,
      label: 'Declining',
      className: 'text-red-600 dark:text-red-400',
    },
    stable: {
      icon: Minus,
      label: 'Stable',
      className: 'text-blue-600 dark:text-blue-400',
    },
    insufficient_data: {
      icon: HelpCircle,
      label: 'More data needed',
      className: 'text-muted-foreground',
    },
  };

  const { icon: Icon, label, className: trendClassName } = config[trend];

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <Icon className={cn('h-4 w-4', trendClassName)} />
      <span className={cn('text-sm font-medium', trendClassName)}>{label}</span>
    </div>
  );
}

// =============================================================================
// Outcome Progress Row Component
// =============================================================================

interface OutcomeProgressRowProps {
  outcome: OutcomeBreakdown;
  onClick?: () => void;
}

function OutcomeProgressRow({ outcome, onClick }: OutcomeProgressRowProps) {
  return (
    <div
      className={cn(
        'p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors',
        onClick && 'cursor-pointer'
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{outcome.outcomeText}</p>
          <p className="text-xs text-muted-foreground">
            {outcome.subject} - {outcome.assessments} assessment{outcome.assessments !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={cn('text-lg font-bold', getMasteryTextColor(outcome.avgPercentage))}>
            {Math.round(outcome.avgPercentage)}%
          </span>
          <MasteryBadge percentage={outcome.avgPercentage} size="sm" />
        </div>
      </div>
      <Progress
        value={outcome.avgPercentage}
        className="h-2"
        style={{
          '--progress-indicator-color': outcome.avgPercentage >= 80
            ? 'rgb(34 197 94)'
            : outcome.avgPercentage >= 50
            ? 'rgb(234 179 8)'
            : 'rgb(239 68 68)',
        } as React.CSSProperties}
      />
    </div>
  );
}

// =============================================================================
// Recent Assessment Row Component
// =============================================================================

interface RecentAssessmentRowProps {
  assessment: RecentAssessment;
  onClick?: () => void;
}

function RecentAssessmentRow({ assessment, onClick }: RecentAssessmentRowProps) {
  const formattedDate = useMemo(() => {
    const date = new Date(assessment.createdAt);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  }, [assessment.createdAt]);

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 transition-colors',
        onClick && 'cursor-pointer'
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      <div className="flex-shrink-0">
        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
          <FileText className="h-4 w-4 text-primary" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{assessment.worksheetTitle}</p>
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {formattedDate}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={cn('font-medium', getMasteryTextColor(assessment.percentage))}>
          {assessment.totalScore}/{assessment.maxScore}
        </span>
        <Badge variant="secondary" className="text-xs">
          {Math.round(assessment.percentage)}%
        </Badge>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function StudentDetailCard({
  data,
  studentName,
  isUnknown = false,
  className,
  onOutcomeClick,
  onAssessmentClick,
}: StudentDetailCardProps) {
  // Sort outcomes by average percentage (lowest first to highlight areas needing attention)
  const sortedOutcomes = useMemo(() => {
    return [...data.outcomes].sort((a, b) => a.avgPercentage - b.avgPercentage);
  }, [data.outcomes]);

  return (
    <Card className={cn('w-full', className)}>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'h-12 w-12 rounded-full flex items-center justify-center text-lg font-semibold',
                isUnknown
                  ? 'bg-muted text-muted-foreground'
                  : 'bg-primary/10 text-primary'
              )}
            >
              {studentName.charAt(0).toUpperCase()}
            </div>
            <div>
              <CardTitle className={cn('text-lg', isUnknown && 'text-muted-foreground')}>
                {studentName}
              </CardTitle>
              <CardDescription>
                {data.totalAssessments} assessment{data.totalAssessments !== 1 ? 's' : ''} completed
              </CardDescription>
            </div>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-1">
            <div className="flex items-center gap-2">
              <span className={cn('text-2xl font-bold', getMasteryTextColor(data.avgPercentage))}>
                {Math.round(data.avgPercentage)}%
              </span>
              <MasteryBadge percentage={data.avgPercentage} />
            </div>
            <TrendIndicator trend={data.recentTrend} />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Outcomes Breakdown */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Target className="h-4 w-4 text-muted-foreground" />
            <h3 className="font-semibold text-sm">Outcome Progress</h3>
            <Badge variant="secondary" className="ml-auto text-xs">
              {data.outcomes.length} outcomes
            </Badge>
          </div>

          {data.outcomes.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No outcome data yet</p>
            </div>
          ) : (
            <ScrollArea className="h-[250px] pr-3">
              <div className="space-y-2">
                {sortedOutcomes.map((outcome) => (
                  <OutcomeProgressRow
                    key={outcome.outcomeId}
                    outcome={outcome}
                    onClick={onOutcomeClick ? () => onOutcomeClick(outcome.outcomeId) : undefined}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        <Separator />

        {/* Recent Assessments */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <h3 className="font-semibold text-sm">Recent Assessments</h3>
            <Badge variant="secondary" className="ml-auto text-xs">
              {data.recentAssessments.length} total
            </Badge>
          </div>

          {data.recentAssessments.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No assessments yet</p>
            </div>
          ) : (
            <ScrollArea className="h-[200px] pr-3">
              <div className="space-y-1">
                {data.recentAssessments.map((assessment) => (
                  <RecentAssessmentRow
                    key={assessment.scanId}
                    assessment={assessment}
                    onClick={onAssessmentClick ? () => onAssessmentClick(assessment.scanId) : undefined}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default StudentDetailCard;
